import { clearUserIdentity, identifyUser } from "@/utils/gleap";
import Swal from "sweetalert2";

const state = {
  user: null,
  token: null,
  authenticating: false,
  authenticated: false,
  sendingEmail: false,
  resettingPassword: false,
  secondStepVerificationEnabled: true,
  userInfo: null,
  onlineUsers: [],
};

const mutations = {
  SET_USER: (state, user) => {
    state.user = user;
  },
  SET_USER_INFO: (state, userInfo) => {
    state.userInfo = userInfo;
  },
  SET_ERROR: (state, error) => {
    state.error = error;
  },
  SET_TOKEN: (state, payload) => {
    state.token = payload;
  },
  SET_AUTHENTICATED: (state, bool) => {
    state.authenticated = bool;
  },
  SET_AUTHENTICATING: (state, bool) => {
    state.authenticating = bool;
  },
  SENDING_RESET_EMAIL: (state, bool) => {
    state.sendingEmail = bool;
  },
  RESETTING_PASSWORD: (state, bool) => {
    state.resettingPassword = bool;
  },
  SET_SECOND_VERIFICATION_ENABLED: (state, bool) => {
    state.secondStepVerificationEnabled = bool;
  },
  SET_ONLINE_USERS: (state, users) => {
    state.onlineUsers = users;
  },
};

const actions = {
  login: function ({ commit }, payload) {
    commit("SET_AUTHENTICATING", true);
    let vm = this;
    return new Promise(function (resolve, reject) {
      vm.$http
        .post(`login`, payload)
        .then((tokenRes) => {
          if (tokenRes.status == 200) {
            if (typeof tokenRes.data.access_token != "undefined") {
              //TODO if the 2step is enabled do not set
              //  token here as it will come from the 2nd step veridication API (suggestion) .
              // need to check with backend develoeprs.
              {
                localStorage.setItem("ARSS_TOKEN", tokenRes.data.access_token);
                localStorage.setItem("ARSS_RF", tokenRes.data.refresh_token);
                commit("SET_TOKEN", tokenRes.data.access_token);
              }
              return resolve(true);
            }
          }
          commit("SET_AUTHENTICATING", false);
          return reject(tokenRes.data);
        })
        .catch((error) => {
          commit("SET_AUTHENTICATING", false);
          return reject(error.response || error);
        });
    });
  },
  setLoggedIn: function (ctx) {
    return new Promise(function (res) {
      if (
        localStorage.getItem("ARSS_TOKEN") &&
        localStorage.getItem("USER_TYPE")
      ) {
        ctx.commit("SET_TOKEN", localStorage.getItem("ARSS_TOKEN"));
        ctx.commit("SET_AUTHENTICATED", true);
        res(true);
      } else {
        ctx.commit("SET_TOKEN", null);
        ctx.commit("SET_AUTHENTICATED", false);
        res(false);
      }
    });
  },
  getUser: function (ctx) {
    return new Promise((resolve, reject) => {
      this.$http
        .get("user")
        .then((res) => {
          if (res.status == 200) {
            ctx.commit("SET_USER", res.data.data);
            // Must follow SET_USER: the action reads the preferences off
            // rootState.
            ctx.dispatch("oliverChatBotV2/hydratePreferences", null, {
              root: true,
            });
            // Keep the Gleap ("Contact Support") identity in sync with the
            // current user, so a name/profile change is reflected without a
            // re-login. getUser runs on app bootstrap, on login, and right
            // after a profile save (My Profile dispatches auth/getUser), so
            // this is the single point that refreshes the widget's name.
            try {
              identifyUser(
                res.data.data,
                (ctx.rootState.settings &&
                  ctx.rootState.settings.companyProfile) ||
                  {}
              );
            } catch (e) {
              console.error("Gleap re-identify failed:", e);
            }
            resolve(res.data.data);
          }
        })
        .catch((error) => {
          if (error.response) {
            if (
              ((error.response.data || {}).error || {}).message ==
                "Unauthenticated." ||
              ((error.response.data || {}).error || {}).message ==
                "token expired"
            ) {
              if (window.location.path != "/login") {
                localStorage.removeItem("ARSS_TOKEN");
                localStorage.removeItem("USER_TYPE");
                localStorage.removeItem("C3_LOGIN");
                localStorage.removeItem("patient_form");
                localStorage.removeItem("task_form");
                localStorage.removeItem("template_form");
                localStorage.removeItem("note_form");
                window.location.href = "/login";
              }
              reject(error);
            }
            reject(error);
          }
        });
    });
  },

  getUserInfo: function (ctx) {
    let vm = this;
    // `reject` was previously unused — a failed request left this promise pending FOREVER,
    // since nothing ever settled it. A caller relying on `.finally()` (e.g. the Documents page
    // behind /admin/documents) then spun on its loading state indefinitely, with no error.
    return new Promise(function (resolve, reject) {
      vm.$http
        .get("user/info")
        .then((res) => {
          if (res.status == 200) {
            ctx.commit("SET_USER_INFO", res.data.data);
            resolve(res.data.data);
          } else {
            reject(res);
          }
        })
        .catch(reject);
    });
  },
  logout: function ({ commit }) {
    let vm = this;
    // Revokes the token server-side; best-effort, fire-and-forget so a stalled or
    // failed call never blocks local logout. Only runs when a token exists -
    // App.vue calls doLogout() on every boot with no token, and calling the API
    // then would 401 into a reload loop (the interceptor hard-redirects to
    // /login on "Unauthenticated.").
    if (localStorage.getItem("ARSS_TOKEN")) {
      vm.$http.post("logout").catch(() => {});
    }

    localStorage.removeItem("ARSS_TOKEN");
    localStorage.removeItem("ARSS_RF");
    localStorage.removeItem("USER_TYPE");
    localStorage.removeItem("C3_LOGIN");
    localStorage.removeItem("patient_form");
    localStorage.removeItem("task_form");
    localStorage.removeItem("template_form");
    localStorage.removeItem("note_form");
    commit("SET_AUTHENTICATED", false);
    commit("SET_AUTHENTICATING", false);
    commit("SET_TOKEN", null);
    commit("SET_USER", null);
    clearUserIdentity();
    return Promise.resolve(true);
  },
  forgotPassword: async function ({ commit }, payload) {
    commit("SENDING_RESET_EMAIL", true);
    try {
      const response = await this.$http.post("/reset-password", payload);
      commit("SENDING_RESET_EMAIL", false);
      if (response.status == 200) {
        return true;
      }
    } catch (err) {
      commit("SENDING_RESET_EMAIL", false);
      throw err;
    }
  },
  resetPassword: async function ({ commit }, payload) {
    commit("RESETTING_PASSWORD", true);
    try {
      const response = await this.$http.post("/reset/password/", payload);
      commit("RESETTING_PASSWORD", false);
      if (response.status == 200) {
        return true;
      }
    } catch (err) {
      commit("RESETTING_PASSWORD", false);
      throw err;
    }
  },

  secondStepVerification: function ({ commit }, verificationData) {
    commit("SET_AUTHENTICATING", true);
    let vm = this;
    return new Promise(function (resolve, reject) {
      vm.$http
        .post(`verifyMFAToken`, verificationData)
        .then((tokenRes) => {
          if (tokenRes.status == 200) {
            if (tokenRes.data.success) {
              commit("SET_AUTHENTICATED", true);
              commit("SET_SECOND_VERIFICATION_ENABLED", false);
              vm.$http.get("user").then((res) => {
                if (res.status == 200) {
                  const user = res.data.data;
                  let userType = "Patient";
                  if (user.isAdmin) {
                    userType = "Admin";
                  } else if (user.isProvider) {
                    userType = "Provider";
                  } else if (user.isManagingAdmin) {
                    userType = "Manager";
                  }
                  localStorage.setItem("USER_TYPE", userType);
                }
              });
              resolve(true);
            }
          }
          commit("SET_AUTHENTICATING", false);
          reject(tokenRes.data);
        })
        .catch((err) => {
          commit("SET_AUTHENTICATING", false);
          reject(err);
        });
    });
  },
  getOnlineUsers: function ({ commit }) {
    let vm = this;
    return new Promise((resolve) => {
      return vm.$http.get(`users/online`).then(
        (res) => {
          commit("SET_ONLINE_USERS", res.data.online_users);
          resolve(res);
        },
        () => {
          resolve(false);
        }
      );
    });
  },
  setOnlineUsers: function ({ commit }, users) {
    commit("SET_ONLINE_USERS", users);
  },
  setAcceptTerms: function (context) {
    context.commit("SET_STATE", { key: "isLoading", value: true });
    let vm = this;
    return new Promise(function (resolve) {
      vm.$http
        .post("user/accept_terms")
        .then((res) => {
          if (res.status == 200) {
            context.commit("SET_USER", res.data.data);
            resolve(res.data.data);
          }
        })
        .catch((err) => {
          if (!err.accessDenied) {
            Swal.fire({
              title: "Error",
              text: (err.data || {}).message || "Something went wrong...",
              icon: "error",
            });
          }
          resolve(err);
        });
    });
  },
};

export default {
  namespaced: true,
  state: state,
  mutations: mutations,
  actions: actions,
};
